import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axiosInstance from '../../utils/api';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#4caf50', '#ff9800', '#f44336'];

export default function AdminDashboard() {
  const navigate = useNavigate();

  // State for statistics
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [chartData, setChartData] = useState({ completion_trend: [], status_distribution: [] });

  // State for questionnaire table
  const [questionnaires, setQuestionnaires] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [cycleFilter, setCycleFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailType, setEmailType] = useState('notification');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyBlankDialogOpen, setCopyBlankDialogOpen] = useState(false);
  const [destCycleId, setDestCycleId] = useState('');
  const [copying, setCopying] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [updating, setUpdating] = useState(false);
  const [availableUserEmails, setAvailableUserEmails] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newQuestionnaireEmail, setNewQuestionnaireEmail] = useState('');
  const [newQuestionnaireProperty, setNewQuestionnaireProperty] = useState('');
  const [newQuestionnaireCycleId, setNewQuestionnaireCycleId] = useState('');
  const [creatingQuestionnaire, setCreatingQuestionnaire] = useState(false);
  const [sendActivation, setSendActivation] = useState(true);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false);
  const [htmlPreviewLoading, setHtmlPreviewLoading] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState(null);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (cycleFilter) params.cycle = cycleFilter;

      const response = await axiosInstance.get('/api/admin/dashboard/stats/', { params });
      setStats(response.data);
      setCycles(response.data.cycles.all || []);
      setChartData(response.data.charts || { completion_trend: [], status_distribution: [] });
      setStatsLoading(false);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setError('Failed to load dashboard statistics');
      setStatsLoading(false);
    }
  };

  // Fetch questionnaires
  const fetchQuestionnaires = async () => {
    try {
      setTableLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      if (statusFilter && statusFilter !== 'stalled') params.status = statusFilter;
      if (statusFilter === 'stalled') params.stalled = true;
      if (cycleFilter) params.cycle = cycleFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await axiosInstance.get('/api/admin/dashboard/questionnaires/', { params });
      setQuestionnaires(response.data.data);
      setTotalCount(response.data.pagination.total_count);
      setTableLoading(false);
    } catch (error) {
      console.error('Failed to fetch questionnaires:', error);
      setError('Failed to load questionnaires');
      setTableLoading(false);
    }
  };

  // Export questionnaires
  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'stalled') params.status = statusFilter;
      if (statusFilter === 'stalled') params.stalled = true;
      if (cycleFilter) params.cycle = cycleFilter;

      const response = await axiosInstance.get('/api/admin/dashboard/export/', {
        params,
        responseType: 'blob'
      });

      // Create a download link for Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PropertyQuestionnaires_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
      setError('Failed to export questionnaires');
    }
  };

  // Fetch available user emails for autocomplete
  const fetchUserEmails = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/dashboard/users/');
      setAvailableUserEmails(response.data.emails);
    } catch (error) {
      console.error('Failed to fetch user emails:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [cycleFilter]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [page, rowsPerPage, statusFilter, cycleFilter, searchQuery]);

  useEffect(() => {
    fetchUserEmails();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleCycleFilterChange = (event) => {
    setCycleFilter(event.target.value);
    setPage(0);
  };

  const handleOpenCreateDialog = () => {
    if (!newQuestionnaireCycleId) {
      const activeCycle = cycles.find((cycle) => cycle.is_active);
      if (activeCycle) {
        setNewQuestionnaireCycleId(activeCycle.id);
      }
    }
    setCreateDialogOpen(true);
  };

  const handleCreateQuestionnaire = async () => {
    if (!newQuestionnaireEmail || !newQuestionnaireProperty) {
      setError('Email and property name are required');
      return;
    }

    try {
      setCreatingQuestionnaire(true);
      const payload = {
        email: newQuestionnaireEmail.trim(),
        property_name: newQuestionnaireProperty.trim(),
      };
      if (newQuestionnaireCycleId) payload.cycle_id = newQuestionnaireCycleId;

      const response = await axiosInstance.post('/api/admin/dashboard/create-questionnaire/', payload);
      const createdId = response.data.questionnaire_id;

      if (sendActivation) {
        await axiosInstance.post('/api/admin/dashboard/send-activation-emails/', {
          questionnaire_ids: [createdId]
        });
      }

      setSuccess(`Questionnaire created for ${response.data.user_email}`);
      setCreateDialogOpen(false);
      setNewQuestionnaireEmail('');
      setNewQuestionnaireProperty('');
      setNewQuestionnaireCycleId('');
      fetchStats();
      fetchQuestionnaires();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to create questionnaire:', error);
      setError(error.response?.data?.error || 'Failed to create questionnaire');
    } finally {
      setCreatingQuestionnaire(false);
    }
  };

  const handleSelectAll = (event) => {
    const currentPageIds = questionnaires.map((q) => q.id);

    if (event.target.checked) {
      // Add all current page items to selection (without removing previous pages)
      setSelectedQuestionnaires((prev) => {
        const newSelections = [...prev];
        currentPageIds.forEach((id) => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        return newSelections;
      });
    } else {
      // Remove all current page items from selection
      setSelectedQuestionnaires((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    }
  };

  const handleSelectOne = (id) => {
    setSelectedQuestionnaires((prev) =>
      prev.includes(id) ? prev.filter((qId) => qId !== id) : [...prev, id]
    );
  };

  const handleSendEmails = async () => {
    if (selectedQuestionnaires.length === 0) {
      setError('Please select at least one questionnaire');
      return;
    }

    await sendEmailsConfirmed();
  };

  const sendEmailsConfirmed = async () => {
    const emailTypeLabels = {
      'notification': 'Questionnaire Notification',
      'follow_up': 'Follow-Up',
      'activation': 'Activation',
      'activation_follow_up': 'Activation Follow-Up'
    };

    if (!window.confirm(`Send "${emailTypeLabels[emailType]}" emails to ${selectedQuestionnaires.length} user(s)?`)) {
      return;
    }

    try {
      setSendingEmails(true);
      const response = await axiosInstance.post('/api/admin/dashboard/send-email/', {
        questionnaire_ids: selectedQuestionnaires,
        email_type: emailType
      });

      setSuccess(`Successfully sent ${response.data.sent_count} ${emailTypeLabels[emailType]} email(s)`);
      if (response.data.failed_count > 0) {
        setError(`Failed to send ${response.data.failed_count} email(s)`);
      }
      setSelectedQuestionnaires([]);
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to send emails:', error);
      setError('Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const handlePreviewEmails = async () => {
    if (selectedQuestionnaires.length === 0) {
      setError('Please select at least one questionnaire');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await axiosInstance.post('/api/admin/dashboard/preview-emails/', {
        questionnaire_ids: selectedQuestionnaires,
        email_type: emailType
      });
      setEmailPreview(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Failed to preview emails:', error);
      setError(error.response?.data?.error || 'Failed to preview emails');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewHtml = async () => {
    try {
      setHtmlPreviewLoading(true);
      const response = await axiosInstance.get('/api/admin/dashboard/preview-email-html/', {
        params: { email_type: emailType }
      });
      setHtmlPreview(response.data);
      setHtmlPreviewOpen(true);
    } catch (error) {
      console.error('Failed to preview email HTML:', error);
      setError(error.response?.data?.error || 'Failed to load email HTML preview');
    } finally {
      setHtmlPreviewLoading(false);
    }
  };


  const handleCopyQuestionnaires = async () => {
    if (selectedQuestionnaires.length === 0) {
      setError('Please select questionnaires to copy');
      return;
    }

    if (!destCycleId) {
      setError('Please select a destination cycle');
      return;
    }

    try {
      setCopying(true);

      const payload = {
        destination_cycle_id: destCycleId,
        questionnaire_ids: selectedQuestionnaires
      };

      const response = await axiosInstance.post('/api/admin/dashboard/copy-questionnaires/', payload);

      setSuccess(`Successfully copied ${response.data.copied_count} questionnaire(s).`);
      setCopyDialogOpen(false);
      setDestCycleId('');
      setSelectedQuestionnaires([]);
      fetchStats();
      fetchQuestionnaires();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to copy questionnaires:', error);
      setError(error.response?.data?.error || 'Failed to copy questionnaires');
    } finally {
      setCopying(false);
    }
  };

  const handleCopyQuestionnairesBlank = async () => {
    if (selectedQuestionnaires.length === 0) {
      setError('Please select questionnaires to copy');
      return;
    }

    if (!destCycleId) {
      setError('Please select a destination cycle');
      return;
    }

    try {
      setCopying(true);

      const payload = {
        destination_cycle_id: destCycleId,
        questionnaire_ids: selectedQuestionnaires
      };

      const response = await axiosInstance.post('/api/admin/dashboard/copy-questionnaires-blank/', payload);

      setSuccess(`Successfully created ${response.data.copied_count} blank questionnaire(s) with property names.`);
      setCopyBlankDialogOpen(false);
      setDestCycleId('');
      setSelectedQuestionnaires([]);
      fetchStats();
      fetchQuestionnaires();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to copy blank questionnaires:', error);
      setError(error.response?.data?.error || 'Failed to copy blank questionnaires');
    } finally {
      setCopying(false);
    }
  };

  const handleEditUserClick = (questionnaire) => {
    setEditingQuestionnaire(questionnaire);
    setNewUserEmail(questionnaire.user_email);
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!newUserEmail || !newUserEmail.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    if (newUserEmail === editingQuestionnaire.user_email) {
      setError('New email is the same as current email');
      return;
    }

    try {
      setUpdating(true);
      const response = await axiosInstance.post('/api/admin/dashboard/update-questionnaire-user/', {
        questionnaire_id: editingQuestionnaire.id,
        new_user_email: newUserEmail
      });

      setSuccess(response.data.message);
      setEditDialogOpen(false);
      setEditingQuestionnaire(null);
      setNewUserEmail('');
      fetchQuestionnaires();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to update user:', error);
      setError(error.response?.data?.error || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isCycleClosed = (cycleEnd) => {
    if (!cycleEnd) return false;
    const endDate = new Date(cycleEnd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= today;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage questionnaires, users, and view statistics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Total Users Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.users.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Active: {stats.users.active} | Inactive: {stats.users.inactive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Questionnaires Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.questionnaires.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Questionnaires
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden' }}>
                  Placeholder
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Completed Questionnaires Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.questionnaires.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.questionnaires.total > 0
                    ? `${Math.round((stats.questionnaires.completed / stats.questionnaires.total) * 100)}%`
                    : '0%'} completion rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* In Progress Questionnaires Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HourglassEmptyIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.questionnaires.in_progress}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In Progress
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden' }}>
                  Placeholder
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Stalled Questionnaires Card */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ReportProblemIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.questionnaires.stalled}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stalled
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Never logged in and not completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Charts */}
      {!statsLoading && chartData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Completion Trend Chart */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Completion Trend (Last 30 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.completion_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#1976d2" name="Completions" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Status Distribution Pie Chart */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.status_distribution.filter(item => item.count > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, count }) => `${status}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {chartData.status_distribution.filter(item => item.count > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Questionnaires Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Questionnaires
          </Typography>

          <TextField
            size="small"
            placeholder="Search by email, name, or property..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="abandoned">Abandoned</MenuItem>
              <MenuItem value="stalled">Stalled (Never Logged In)</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Cycle</InputLabel>
            <Select
              value={cycleFilter}
              label="Cycle"
              onChange={handleCycleFilterChange}
            >
              <MenuItem value="">All Cycles</MenuItem>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {new Date(cycle.date_opened).toLocaleDateString()}
                  {cycle.date_closed ? ` - ${new Date(cycle.date_closed).toLocaleDateString()}` : ' (Open)'}
                  {cycle.is_active && ' 🟢'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Refresh">
            <IconButton onClick={() => { fetchStats(); fetchQuestionnaires(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>


          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export Excel
          </Button>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Email Type</InputLabel>
            <Select
              value={emailType}
              label="Email Type"
              onChange={(e) => setEmailType(e.target.value)}
            >
              <MenuItem value="notification">Questionnaire Notification</MenuItem>
              <MenuItem value="follow_up">Follow-Up</MenuItem>
              <MenuItem value="activation">Activation Link</MenuItem>
              <MenuItem value="activation_follow_up">Activation Follow-Up</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<EmailIcon />}
            onClick={handleSendEmails}
            disabled={selectedQuestionnaires.length === 0 || sendingEmails}
          >
            Send Email ({selectedQuestionnaires.length})
          </Button>

          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handlePreviewEmails}
            disabled={selectedQuestionnaires.length === 0 || previewLoading}
          >
            {previewLoading ? 'Loading Preview...' : 'Preview Email'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handlePreviewHtml}
            disabled={htmlPreviewLoading}
          >
            {htmlPreviewLoading ? 'Loading HTML...' : 'Preview HTML'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<FileCopyIcon />}
            onClick={() => setCopyDialogOpen(true)}
            disabled={selectedQuestionnaires.length === 0}
          >
            Copy with Data ({selectedQuestionnaires.length})
          </Button>

          <Button
            variant="outlined"
            color="info"
            startIcon={<FileCopyIcon />}
            onClick={() => setCopyBlankDialogOpen(true)}
            disabled={selectedQuestionnaires.length === 0}
          >
            Copy Blank ({selectedQuestionnaires.length})
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedQuestionnaires.length > 0 && selectedQuestionnaires.length < questionnaires.length}
                    checked={questionnaires.length > 0 && selectedQuestionnaires.length === questionnaires.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Property Name</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Has Logged In</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Cycle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : questionnaires.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No questionnaires found
                  </TableCell>
                </TableRow>
              ) : (
                questionnaires.map((q) => (
                  <TableRow
                    key={q.id}
                    hover
                    selected={selectedQuestionnaires.includes(q.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedQuestionnaires.includes(q.id)}
                        onChange={() => handleSelectOne(q.id)}
                      />
                    </TableCell>
                    <TableCell>{q.property_name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {q.user_email}
                        {q.status !== 'completed' && !isCycleClosed(q.cycle_end) && (
                          <Tooltip title="Change user/email">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUserClick(q)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {q.user_has_logged_in ? (
                          <Chip
                            label="Yes"
                            color="success"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Chip
                            label="No"
                            color="warning"
                            size="small"
                            icon={<WarningIcon />}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{q.person_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={q.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(q.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={q.risk_flags?.length ? q.risk_flags.join(', ') : 'No risk flags'}>
                        <Chip
                          label={q.risk_score ? `Score ${q.risk_score}` : 'None'}
                          color={q.risk_score ? 'warning' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={q.completion_percentage}
                          sx={{ width: 100, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption">
                          {q.completed_count}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(q.date_created)}</TableCell>
                    <TableCell>{formatDate(q.date_completed)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        Start: {formatDate(q.cycle_start)}
                      </Typography>
                      {q.cycle_end && (
                        <Typography variant="caption" display="block">
                          End: {formatDate(q.cycle_end)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Copy Questionnaires Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy Selected Questionnaires</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Copy {selectedQuestionnaires.length} selected questionnaire(s) to another cycle. Users will have pre-filled questionnaires in the destination cycle.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Destination Cycle</InputLabel>
            <Select
              value={destCycleId}
              label="Destination Cycle"
              onChange={(e) => setDestCycleId(e.target.value)}
            >
              <MenuItem value="">Select a cycle</MenuItem>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {new Date(cycle.date_opened).toLocaleDateString()}
                  {cycle.date_closed ? ` - ${new Date(cycle.date_closed).toLocaleDateString()}` : ' (Open)'}
                  {cycle.is_active && ' 🟢'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCopyQuestionnaires}
            variant="contained"
            disabled={!destCycleId || copying}
            startIcon={copying ? <CircularProgress size={20} /> : <FileCopyIcon />}
          >
            {copying ? 'Copying...' : `Copy ${selectedQuestionnaires.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Blank Questionnaires Dialog */}
      <Dialog open={copyBlankDialogOpen} onClose={() => setCopyBlankDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy Blank Questionnaires</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Create blank questionnaires with only the property name and user association. All other fields will be empty for users to fill in fresh.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This will create {selectedQuestionnaires.length} blank questionnaire(s) in the destination cycle.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Destination Cycle</InputLabel>
            <Select
              value={destCycleId}
              label="Destination Cycle"
              onChange={(e) => setDestCycleId(e.target.value)}
            >
              <MenuItem value="">Select a cycle</MenuItem>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {new Date(cycle.date_opened).toLocaleDateString()}
                  {cycle.date_closed ? ` - ${new Date(cycle.date_closed).toLocaleDateString()}` : ' (Open)'}
                  {cycle.is_active && ' 🟢'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyBlankDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCopyQuestionnairesBlank}
            variant="contained"
            disabled={!destCycleId || copying}
            startIcon={copying ? <CircularProgress size={20} /> : <FileCopyIcon />}
          >
            {copying ? 'Creating...' : `Create ${selectedQuestionnaires.length} Blank`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Email Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Questionnaire User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Change the user/email associated with this questionnaire. The new user must already exist in the system.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Note: You cannot change the user for completed questionnaires or questionnaires in closed cycles.
          </Alert>

          {editingQuestionnaire && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Current User:</strong> {editingQuestionnaire.user_email}
                </Typography>
                <Typography variant="body2">
                  <strong>Property:</strong> {editingQuestionnaire.property_name}
                </Typography>
              </Alert>

              <Autocomplete
                fullWidth
                freeSolo
                options={availableUserEmails}
                value={newUserEmail}
                onInputChange={(event, newValue) => setNewUserEmail(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New User Email"
                    type="email"
                    sx={{ mt: 2 }}
                    helperText="Start typing to search for existing users"
                  />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={!newUserEmail || updating}
            startIcon={updating ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {updating ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Questionnaire Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Questionnaire</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a blank questionnaire by entering the user email and asset name.
            If the user does not exist, they will be created as inactive.
          </Typography>

          <TextField
            fullWidth
            label="User Email"
            type="email"
            value={newQuestionnaireEmail}
            onChange={(e) => setNewQuestionnaireEmail(e.target.value)}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Asset / Property Name"
            value={newQuestionnaireProperty}
            onChange={(e) => setNewQuestionnaireProperty(e.target.value)}
            sx={{ mt: 2 }}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Cycle</InputLabel>
            <Select
              value={newQuestionnaireCycleId}
              label="Cycle"
              onChange={(e) => setNewQuestionnaireCycleId(e.target.value)}
            >
              <MenuItem value="">Active Cycle (default)</MenuItem>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={cycle.id}>
                  {new Date(cycle.date_opened).toLocaleDateString()}
                  {cycle.date_closed ? ` - ${new Date(cycle.date_closed).toLocaleDateString()}` : ' (Open)'}
                  {cycle.is_active && ' 🟢'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={sendActivation}
                onChange={(e) => setSendActivation(e.target.checked)}
              />
            }
            label="Send activation email if user has never logged in"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateQuestionnaire}
            variant="contained"
            disabled={creatingQuestionnaire}
            startIcon={creatingQuestionnaire ? <CircularProgress size={20} /> : <AddCircleOutlineIcon />}
          >
            {creatingQuestionnaire ? 'Creating...' : 'Create Questionnaire'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview (Template Data)</DialogTitle>
        <DialogContent>
          {!emailPreview ? (
            <Typography variant="body2" color="text.secondary">
              No preview data available.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This shows the SendGrid template ID and dynamic data that will be sent.
              </Typography>
              <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }}>
                {JSON.stringify(emailPreview, null, 2)}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email HTML Preview Dialog */}
      <Dialog open={htmlPreviewOpen} onClose={() => setHtmlPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Email HTML Preview</DialogTitle>
        <DialogContent>
          {!htmlPreview ? (
            <Typography variant="body2" color="text.secondary">
              No HTML preview available.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Template: {htmlPreview.template_name} ({htmlPreview.template_id}) • Version: {htmlPreview.version_name}
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: 600,
                  backgroundColor: '#fafafa'
                }}
              >
                <iframe
                  title="Email HTML Preview"
                  style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }}
                  srcDoc={htmlPreview.html_content || '<p>No HTML content in this template.</p>'}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHtmlPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}
